import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import FeaturedCarousel from '../components/FeaturedCarousel';
import SubscribeWidget from '../components/SubscribeWidget';
import { api, Post } from '../utils/api';
import { useLanguage } from '../contexts/LanguageContext';

function stripHtml(html: string): string {
  return html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').trim();
}

const categoryColors: Record<string, { bg: string; color: string; circleBg: string }> = {
  'Annual Gala': { bg: 'var(--red-bg)', color: 'var(--red-primary)', circleBg: 'var(--red-primary)' },
  Weekly: { bg: 'var(--blue-bg)', color: 'var(--blue-accent)', circleBg: 'var(--blue-accent)' },
  Workshops: { bg: 'var(--yellow-bg)', color: 'var(--yellow-accent)', circleBg: 'var(--yellow-accent)' },
  Community: { bg: 'var(--green-bg)', color: 'var(--green-accent)', circleBg: 'var(--green-accent)' },
  Education: { bg: 'var(--blue-bg)', color: 'var(--blue-accent)', circleBg: 'var(--blue-accent)' },
  Social: { bg: 'var(--pink-bg)', color: 'var(--pink-accent)', circleBg: 'var(--pink-accent)' },
  Cultural: { bg: 'var(--orange-bg)', color: 'var(--orange-accent)', circleBg: 'var(--orange-accent)' },
};

const fallbackEvents: Post[] = [
  {
    id: 10,
    title: 'Festive Feast 2026',
    subtitle: 'Annual Community Gala Dinner',
    slug: 'festive-feast-2026',
    content: 'Join us for CAAOC\'s signature annual gala dinner with live entertainment, traditional cuisine, and community awards.',
    post_type: 'event',
    status: 'published',
    featured: 1,
    event_date: '2026-05-15',
    event_time: '5:00 PM - 9:00 PM',
    event_location: 'Irvine Marriott Grand Ballroom',
    category: 'Annual Gala',
    button1_text: 'Get Tickets',
    button1_url: '/events',
    ticket_url: '#',
    author_id: 1,
    created_at: '2026-01-01T00:00:00Z',
    updated_at: '2026-01-01T00:00:00Z',
  },
  {
    id: 11,
    title: 'Tai Chi in the Park',
    subtitle: 'Weekly Wellness Session',
    slug: 'tai-chi-park',
    content: 'Start your weekend with guided Tai Chi practice in a beautiful outdoor setting. All levels welcome.',
    post_type: 'event',
    status: 'published',
    featured: 0,
    event_date: '2026-04-12',
    event_time: '8:00 AM - 9:30 AM',
    event_location: 'Heritage Park, Irvine',
    category: 'Weekly',
    author_id: 1,
    created_at: '2026-01-01T00:00:00Z',
    updated_at: '2026-01-01T00:00:00Z',
  },
  {
    id: 12,
    title: 'Chinese Calligraphy Workshop',
    subtitle: 'Learn the Art of Brushwork',
    slug: 'calligraphy-workshop',
    content: 'Master the fundamentals of Chinese calligraphy with expert instructor Liu Wei. Materials provided.',
    post_type: 'event',
    status: 'published',
    featured: 0,
    event_date: '2026-04-18',
    event_time: '2:00 PM - 4:00 PM',
    event_location: 'CAAOC Community Center',
    category: 'Workshops',
    author_id: 1,
    created_at: '2026-01-01T00:00:00Z',
    updated_at: '2026-01-01T00:00:00Z',
  },
  {
    id: 13,
    title: 'Community BBQ Picnic',
    subtitle: 'Family Fun Day',
    slug: 'community-bbq',
    content: 'Bring the whole family for a day of food, games, and socializing with fellow community members.',
    post_type: 'event',
    status: 'published',
    featured: 0,
    event_date: '2026-04-25',
    event_time: '11:00 AM - 3:00 PM',
    event_location: 'Mason Regional Park',
    category: 'Community',
    author_id: 1,
    created_at: '2026-01-01T00:00:00Z',
    updated_at: '2026-01-01T00:00:00Z',
  },
  {
    id: 14,
    title: 'SAT Prep Seminar',
    subtitle: 'College Prep Series',
    slug: 'sat-prep',
    content: 'Expert-led SAT preparation strategies for high school students. Tips on time management, test-taking, and study plans.',
    post_type: 'event',
    status: 'published',
    featured: 0,
    event_date: '2026-05-02',
    event_time: '10:00 AM - 12:00 PM',
    event_location: 'Irvine Public Library',
    category: 'Education',
    author_id: 1,
    created_at: '2026-01-01T00:00:00Z',
    updated_at: '2026-01-01T00:00:00Z',
  },
  {
    id: 15,
    title: 'Dragon Boat Festival',
    subtitle: 'Annual Race & Celebration',
    slug: 'dragon-boat',
    content: 'Watch exciting dragon boat races and enjoy cultural performances, crafts, and traditional festival food.',
    post_type: 'event',
    status: 'published',
    featured: 0,
    event_date: '2026-06-14',
    event_time: '9:00 AM - 5:00 PM',
    event_location: 'Newport Dunes',
    category: 'Community',
    author_id: 1,
    created_at: '2026-01-01T00:00:00Z',
    updated_at: '2026-01-01T00:00:00Z',
  },
];

const fallbackPastEvents: Post[] = [
  {
    id: 20,
    title: 'Lunar New Year Gala 2026',
    subtitle: 'Year of the Horse Celebration',
    slug: 'lny-gala-2026',
    content: 'A spectacular evening celebrating the Lunar New Year with performances, dinner, and cultural activities.',
    post_type: 'event',
    status: 'published',
    featured: 0,
    event_date: '2026-01-29',
    event_time: '6:00 PM - 10:00 PM',
    event_location: 'Hilton Irvine',
    category: 'Annual Gala',
    author_id: 1,
    created_at: '2025-12-01T00:00:00Z',
    updated_at: '2025-12-01T00:00:00Z',
    video_url: 'https://youtube.com',
  },
  {
    id: 21,
    title: 'Winter Charity Drive',
    subtitle: 'Giving Back to the Community',
    slug: 'winter-charity',
    content: 'Our annual winter charity drive collecting donations for local families in need.',
    post_type: 'event',
    status: 'published',
    featured: 0,
    event_date: '2025-12-15',
    event_time: '10:00 AM - 2:00 PM',
    event_location: 'CAAOC Community Center',
    category: 'Community',
    author_id: 1,
    created_at: '2025-11-01T00:00:00Z',
    updated_at: '2025-11-01T00:00:00Z',
  },
  {
    id: 22,
    title: 'Mid-Autumn Moon Festival',
    subtitle: 'Celebrate Under the Stars',
    slug: 'moon-festival',
    content: 'Join us for mooncakes, lanterns, and traditional performances under the harvest moon.',
    post_type: 'event',
    status: 'published',
    featured: 0,
    event_date: '2025-09-28',
    event_time: '5:00 PM - 9:00 PM',
    event_location: 'Great Park, Irvine',
    category: 'Cultural',
    author_id: 1,
    created_at: '2025-08-01T00:00:00Z',
    updated_at: '2025-08-01T00:00:00Z',
    video_url: 'https://youtube.com',
  },
];

const weeklySchedule = [
  { day: 'Monday', items: [{ time: '7:00 PM', name: 'Line Dancing', location: 'Community Center' }] },
  { day: 'Tuesday', items: [{ time: '9:00 AM', name: 'Tai Chi', location: 'Heritage Park' }] },
  { day: 'Wednesday', items: [{ time: '10:00 AM', name: 'Chinese Painting', location: 'Art Studio' }, { time: '7:00 PM', name: 'Choir Practice', location: 'Community Center' }] },
  { day: 'Thursday', items: [{ time: '2:00 PM', name: 'Mahjong Club', location: 'Senior Center' }] },
  { day: 'Friday', items: [{ time: '6:30 PM', name: 'Karaoke Night', location: 'Community Center' }] },
  { day: 'Saturday', items: [{ time: '8:00 AM', name: 'Tai Chi in the Park', location: 'Heritage Park' }, { time: '10:00 AM', name: 'Chinese Language School', location: 'Irvine Library' }] },
  { day: 'Sunday', items: [{ time: '10:00 AM', name: 'Badminton', location: 'Gym' }] },
];

const dayTranslations: Record<string, string> = {
  Monday: '周一',
  Tuesday: '周二',
  Wednesday: '周三',
  Thursday: '周四',
  Friday: '周五',
  Saturday: '周六',
  Sunday: '周日',
};

function formatEventDate(dateStr?: string, locale?: string): { month: string; day: string } {
  if (!dateStr) return { month: 'TBD', day: '' };
  const d = new Date(dateStr + 'T00:00:00');
  return {
    month: d.toLocaleDateString(locale === 'zh' ? 'zh-CN' : 'en-US', { month: 'short' }).toUpperCase(),
    day: String(d.getDate()),
  };
}

function EventCard({
  event,
  isPast = false,
  locale = 'en',
  t,
}: {
  event: Post;
  isPast?: boolean;
  locale?: string;
  t: (key: string) => string;
}) {
  const dateInfo = formatEventDate(event.event_date, locale);
  const catStyle = categoryColors[event.category || ''] || categoryColors['Community'];

  return (
    <div
      className="card"
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        opacity: isPast ? 0.85 : 1,
      }}
    >
      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
        {/* Month badge circle */}
        <div
          style={{
            width: 56,
            height: 56,
            borderRadius: '50%',
            background: isPast ? 'var(--gray-300)' : catStyle.circleBg,
            color: '#fff',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            lineHeight: 1,
          }}
        >
          <span style={{ fontSize: '0.6rem', fontWeight: 700, letterSpacing: 0.5 }}>{dateInfo.month}</span>
          <span style={{ fontSize: '1.2rem', fontWeight: 800 }}>{dateInfo.day}</span>
        </div>

        <div style={{ flex: 1 }}>
          <h3 style={{ fontSize: '1.1rem', fontWeight: 700, color: 'var(--gray-900)', marginBottom: 4 }}>
            {event.title}
          </h3>
          <p style={{ fontSize: '0.85rem', color: 'var(--gray-500)', marginBottom: 4 }}>
            {event.event_date
              ? new Date(event.event_date + 'T00:00:00').toLocaleDateString(locale === 'zh' ? 'zh-CN' : 'en-US', {
                  weekday: 'long',
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric',
                })
              : 'Date TBD'}
          </p>
        </div>
      </div>

      {event.content && (
        <p style={{ fontSize: '0.9rem', color: 'var(--gray-600)', lineHeight: 1.5 }}>
          {stripHtml(event.content).length > 120 ? stripHtml(event.content).substring(0, 120) + '...' : stripHtml(event.content)}
        </p>
      )}

      {event.event_location && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.85rem', color: 'var(--gray-500)' }}>
          <span>&#128205;</span>
          <span>{event.event_location}</span>
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 'auto' }}>
        {event.ticket_url && !isPast && (
          <a
            href={event.ticket_url}
            className="btn btn-primary"
            style={{ padding: '8px 16px', fontSize: '0.85rem' }}
          >
            {t('home.getTickets')}
          </a>
        )}
        {event.video_url && (
          <a
            href={event.video_url}
            className="btn btn-outline"
            style={{ padding: '8px 16px', fontSize: '0.85rem' }}
            target="_blank"
            rel="noopener noreferrer"
          >
            &#9654; {t('home.watchVideo')}
          </a>
        )}
        {event.category && (
          <span
            className="badge"
            style={{
              background: catStyle.bg,
              color: catStyle.color,
              marginLeft: 'auto',
            }}
          >
            {event.category}
          </span>
        )}
      </div>
    </div>
  );
}

export default function HomePage() {
  const { locale, t, prefix } = useLanguage();
  const [events, setEvents] = useState<Post[]>(fallbackEvents);
  const [pastEvents, setPastEvents] = useState<Post[]>(fallbackPastEvents);
  const [activeFilter, setActiveFilter] = useState('All Events');

  const filterTabs = [
    { key: 'All Events', label: t('home.allEvents') },
    { key: 'Annual Gala', label: t('home.annualGala') },
    { key: 'Weekly', label: t('home.weekly') },
    { key: 'Workshops', label: t('home.workshops') },
    { key: 'Community', label: t('home.community') },
    { key: 'Education', label: t('home.education') },
  ];

  useEffect(() => {
    const localeParam = locale === 'zh' ? '&locale=zh' : '&locale=en';
    api.get<{ posts: Post[] }>(`/posts?post_type=event&status=published&limit=20${localeParam}`)
      .then((data) => {
        if (data.posts && data.posts.length > 0) {
          const now = new Date().toISOString().split('T')[0];
          const upcoming = data.posts.filter(
            (p) => !p.event_date || p.event_date >= now
          );
          const past = data.posts.filter(
            (p) => p.event_date && p.event_date < now
          );
          if (upcoming.length > 0) setEvents(upcoming);
          if (past.length > 0) setPastEvents(past);
        }
      })
      .catch(() => {});
  }, [locale]);

  const filteredEvents =
    activeFilter === 'All Events'
      ? events
      : events.filter((e) => e.category === activeFilter);

  return (
    <div>
      <FeaturedCarousel />

      {/* Events & Activities Section */}
      <section style={{ padding: 'var(--section-padding)', background: 'var(--gray-100)' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: 40 }}>
            <h2 className="section-title">{t('home.eventsAndActivities')}</h2>
            <p className="section-subtitle" style={{ margin: '0 auto' }}>
              {t('home.eventsSubtitle')}
            </p>
          </div>

          {/* Filter tabs */}
          <div
            style={{
              display: 'flex',
              gap: 8,
              marginBottom: 32,
              flexWrap: 'wrap',
              justifyContent: 'center',
            }}
          >
            {filterTabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveFilter(tab.key)}
                style={{
                  padding: '8px 20px',
                  borderRadius: 24,
                  border: 'none',
                  background: activeFilter === tab.key ? 'var(--red-primary)' : 'var(--white)',
                  color: activeFilter === tab.key ? '#fff' : 'var(--gray-600)',
                  fontWeight: 600,
                  fontSize: '0.9rem',
                  cursor: 'pointer',
                  boxShadow: activeFilter === tab.key ? 'var(--shadow-md)' : 'var(--shadow-sm)',
                  transition: 'all 0.2s ease',
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Event cards grid */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
              gap: 24,
            }}
          >
            {filteredEvents.map((event) => (
              <EventCard key={event.id} event={event} locale={locale} t={t} />
            ))}
          </div>

          {filteredEvents.length === 0 && (
            <div style={{ textAlign: 'center', padding: 60, color: 'var(--gray-500)' }}>
              {t('home.noEventsInCategory')}
            </div>
          )}

          <div style={{ textAlign: 'center', marginTop: 40 }}>
            <Link to={prefix + '/events'} className="btn btn-outline" style={{ padding: '12px 32px' }}>
              {t('home.viewAllEvents')}
            </Link>
          </div>
        </div>
      </section>

      {/* Past Events Section */}
      <section style={{ padding: 'var(--section-padding)', background: 'var(--white)' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: 40 }}>
            <h2 className="section-title">{t('home.pastEvents')}</h2>
            <p className="section-subtitle" style={{ margin: '0 auto' }}>
              {t('home.pastEventsSubtitle')}
            </p>
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
              gap: 24,
            }}
          >
            {pastEvents.slice(0, 6).map((event) => (
              <EventCard key={event.id} event={event} isPast locale={locale} t={t} />
            ))}
          </div>
        </div>
      </section>

      {/* Weekly Schedule Section */}
      <section style={{ padding: 'var(--section-padding)', background: 'var(--gray-100)' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: 40 }}>
            <h2 className="section-title">{t('home.weeklySchedule')}</h2>
            <p className="section-subtitle" style={{ margin: '0 auto' }}>
              {t('home.regularRecurring')}
            </p>
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
              gap: 16,
            }}
          >
            {weeklySchedule.map((day) => (
              <div
                key={day.day}
                className="card"
                style={{ padding: 20, textAlign: 'center' }}
              >
                <div
                  style={{
                    fontWeight: 700,
                    fontSize: '0.95rem',
                    color: 'var(--red-primary)',
                    marginBottom: 12,
                    paddingBottom: 8,
                    borderBottom: '2px solid var(--red-light)',
                  }}
                >
                  {locale === 'zh' ? dayTranslations[day.day] : day.day}
                </div>
                {day.items.map((item, idx) => (
                  <div key={idx} style={{ marginBottom: idx < day.items.length - 1 ? 12 : 0 }}>
                    <div style={{ fontSize: '0.8rem', color: 'var(--gray-500)', marginBottom: 2 }}>
                      {item.time}
                    </div>
                    <div style={{ fontWeight: 600, fontSize: '0.9rem', color: 'var(--gray-800)' }}>
                      {item.name}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>
                      {item.location}
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>

          <div style={{ textAlign: 'center', marginTop: 32 }}>
            <Link to={prefix + '/schedule'} className="btn btn-outline" style={{ padding: '12px 32px' }}>
              {t('home.fullSchedule')}
            </Link>
          </div>
        </div>
      </section>

      {/* About CAAOC Section */}
      <section style={{ padding: 'var(--section-padding)', background: 'var(--white)' }}>
        <div className="container">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 60, alignItems: 'center' }}>
            <div>
              <h2 className="section-title" style={{ marginBottom: 20 }}>{t('home.aboutCAAOC')}</h2>
              <p style={{ fontSize: '1.05rem', color: 'var(--gray-600)', lineHeight: 1.7, marginBottom: 16 }}>
                {t('home.aboutText1')}
              </p>
              <p style={{ fontSize: '1.05rem', color: 'var(--gray-600)', lineHeight: 1.7, marginBottom: 32 }}>
                {t('home.aboutText2')}
              </p>
              <Link to={prefix + '/about'} className="btn btn-primary" style={{ padding: '12px 32px' }}>
                {t('home.learnMore')}
              </Link>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              {[
                { labelKey: 'home.community', descKey: 'home.communitySubtitle', color: 'var(--red-primary)', bg: 'var(--red-bg)' },
                { labelKey: 'nav.events', descKey: 'home.eventsBoxDesc', color: 'var(--yellow-accent)', bg: 'var(--yellow-bg)' },
                { labelKey: 'home.education', descKey: 'home.educationBoxDesc', color: 'var(--blue-accent)', bg: 'var(--blue-bg)' },
                { labelKey: 'home.schools', descKey: 'home.schoolsBoxDesc', color: 'var(--green-accent)', bg: 'var(--green-bg)' },
              ].map((box) => (
                <div
                  key={box.labelKey}
                  style={{
                    background: box.bg,
                    borderRadius: 'var(--radius-md)',
                    padding: 24,
                    borderLeft: `4px solid ${box.color}`,
                  }}
                >
                  <div style={{ fontWeight: 700, color: box.color, fontSize: '1rem', marginBottom: 8 }}>
                    {t(box.labelKey)}
                  </div>
                  <p style={{ fontSize: '0.85rem', color: 'var(--gray-600)', lineHeight: 1.5 }}>
                    {t(box.descKey)}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Stats */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: 32,
              marginTop: 64,
              textAlign: 'center',
            }}
          >
            {[
              { number: '15+', label: t('home.years') },
              { number: '1000+', label: t('home.members') },
              { number: '50+', label: t('home.eventsPerYear') },
            ].map((stat) => (
              <div key={stat.label}>
                <div
                  style={{
                    fontSize: '3rem',
                    fontWeight: 900,
                    color: 'var(--red-primary)',
                    lineHeight: 1,
                    marginBottom: 8,
                  }}
                >
                  {stat.number}
                </div>
                <div style={{ fontSize: '1rem', color: 'var(--gray-600)', fontWeight: 500 }}>
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Subscribe */}
      <section style={{ padding: '20px 0 60px', background: 'var(--gray-100)' }}>
        <div className="container">
          <SubscribeWidget />
        </div>
      </section>

      <style>{`
        @media (max-width: 768px) {
          .section-title { text-align: center; }
        }
      `}</style>
    </div>
  );
}
